import { Fragment } from "react";
import { DivIcon, LatLng, LeafletMouseEvent } from "leaflet";
import { Marker } from "react-leaflet";

import { Coordinates } from "../../types/MapData";
import { createPinLabelIcon } from "../../MapUtil";

export type PinData = {
  point: Coordinates;
  label?: string;
  color?: string;
};

type Props = {
  pins?: PinData[];
  selectedPin?: PinData;
  selectedPins?: PinData[];
  onClicked?: (pin: PinData) => void;
};

const pointPinIconCache = new Map<string, DivIcon>();

const createPointPinIcon = (color?: string, selected?: boolean) => {
  const cacheKey = `${color ?? ""}:${selected ? "1" : "0"}`;
  const cached = pointPinIconCache.get(cacheKey);
  if (cached) return cached;

  const style = color ? ` style="--pin-color: ${color}"` : "";
  const size = selected ? 22 : 18;
  const anchor = selected ? 11 : 9;
  const selectedClass = selected ? " is-selected" : "";

  const icon = new DivIcon({
    className: "select-point-pin-icon",
    html: `<div class="select-point-pin${selectedClass}"${style}><div class="select-point-pin__dot"></div></div>`,
    iconSize: [size, size],
    iconAnchor: [anchor, anchor],
  });

  pointPinIconCache.set(cacheKey, icon);
  return icon;
};

export const PinDrawer = (props: Props) => {
  const { pins, selectedPin, selectedPins, onClicked } = props;
  const isClickable = !!onClicked;
  const isSamePin = (a: PinData, b?: PinData) => {
    if (!b) return false;
    return a.point.x === b.point.x && a.point.y === b.point.y;
  };

  return (
    <>
      {pins?.map((pin) => {
        const isSelected =
          isSamePin(pin, selectedPin) ||
          !!selectedPins?.some((selected) => isSamePin(pin, selected));
        return (
          <Fragment key={`pin-${pin.point.x}-${pin.point.y}`}>
            <Marker
              position={new LatLng(pin.point.y, pin.point.x)}
              icon={createPointPinIcon(pin.color, isSelected)}
              interactive={isClickable}
              zIndexOffset={500}
              eventHandlers={
                onClicked
                  ? {
                      click: (e: LeafletMouseEvent) => {
                        e.originalEvent.stopPropagation();
                        onClicked(pin);
                      },
                    }
                  : undefined
              }
            />
            {pin.label?.trim() && (
              <Marker
                key={`label-${pin.point.x}-${pin.point.y}-${pin.label}`}
                position={new LatLng(pin.point.y, pin.point.x)}
                icon={createPinLabelIcon(pin.label, isSelected)}
                interactive={isClickable}
                zIndexOffset={500}
                eventHandlers={
                  onClicked
                    ? {
                        click: (e: LeafletMouseEvent) => {
                          e.originalEvent.stopPropagation();
                          onClicked(pin);
                        },
                      }
                    : undefined
                }
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};
